import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, DealSizeTrendPoint } from '../../services/analyticsService';

interface DealSizeTrendChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const formatPeriod = (period: string): string => {
  try {
    const date = new Date(period);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = String(date.getFullYear()).slice(2);
    return `${month} '${year}`;
  } catch {
    return period;
  }
};

const formatCrore = (valueLakhs: number): string => {
  if (valueLakhs >= 100) {
    return `\u20B9${(valueLakhs / 100).toFixed(1)} Cr`;
  }
  return `\u20B9${valueLakhs.toFixed(1)} L`;
};

const DealSizeTrendChart: React.FC<DealSizeTrendChartProps> = ({
  businessIdentifier,
  timeRange,
}) => {
  const [data, setData] = useState<DealSizeTrendPoint[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessIdentifier) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const groupBy = timeRange === '7d' ? 'week' : 'month';
        const result = await analyticsService.getDealSizeTrend(
          businessIdentifier,
          timeRange || '90d',
          groupBy
        );
        setData(result.data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch deal size trend data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (!data || data.length === 0) return {};

    const periods = data.map((d) => formatPeriod(d.period));
    const valuesInLakhs = data.map((d) => d.avg_deal_value / 100000);

    return {
      tooltip: {
        trigger: 'axis',
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const item = data[idx];
          if (!item) return '';
          const lakhs = item.avg_deal_value / 100000;
          return `<b>${formatPeriod(item.period)}</b><br/>Avg Deal: ${formatCrore(lakhs)} (${item.count} deals)`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: periods,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        name: 'Deal Value (Lakhs)',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      series: [
        {
          type: 'line',
          data: valuesInLakhs,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            color: '#4A90D9',
            width: 2,
          },
          itemStyle: {
            color: '#4A90D9',
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(74, 144, 217, 0.3)' },
                { offset: 1, color: 'rgba(74, 144, 217, 0.02)' },
              ],
            },
          },
        },
      ],
    };
  };

  const hasData = data && data.length > 0;

  return (
    <ChartCard title="Average Deal Size Trend" isLoading={isLoading} error={error}>
      {hasData ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '350px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            Coming Soon — Data will appear as new conversations are processed
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default DealSizeTrendChart;
